<template>
  <div v-if="planMedia">
    <div class="entete">
      <div class="columns mgb-0">
        <div class="column pdb-0">
          <h1 class="title is-size-3">
            {{ getTitle }}
          </h1>
        </div>
        <div class="column is-narrow pdb-0">
          <div class="is-pulled-right buttons boutonAction">
            <button
              @click="cancel()"
              class="button btn-modal-annuler is-cancel">
              {{ $t('general.button.cancel') }}
            </button>
            <div
              @click="save()"
              class="button is-addOrEdit">
              <span> {{ $t('general.button.sauvegarder') }} </span>
            </div>
              <dropdownButton
                type="is-addOrEdit"
                :permission="permCommandes.maj"
                :actions="actionSecondary"
                @click="addDevisCommande(constantes.TYPE_COMMANDE)">
                  {{ $t('addEditPlanMedia.commande.actions.addCommande') }}
              </dropdownButton>
          </div>
        </div>
      </div>
      <breadcrumb :breadcrumb="breadcrumb"/>
      <error-msg-component
        :messages="messages"
        :error="error"
        :messagesChampsVide="messagesChampsVide"
        :champsVide="champsVide"
        @close="error = $event"
        title="editionClient.problemOccurred"
        withEmplyFields/>
      <div class="tab">
        <ul>
          <li :class="{'is-active': activeTab === 'information'}"
              @click="activeTab = 'information'">
            {{ $t('addEditPlanMedia.tab.information') }}
          </li>
          <li :class="{'is-active': activeTab === 'evenement'}"
              @click="activeTab = 'evenement'">
            {{ $t('addEditPlanMedia.tab.evenement') }}
          </li>
          <li :class="{'is-active': activeTab === 'infoVentes'}"
              @click="activeTab = 'infoVentes'"
              v-if="$route.params.id">
            {{ $t('addEditPlanMedia.tab.commande') }}
          </li>
          <li :class="{'is-active': activeTab === 'commissions'}"
              @click="activeTab = 'commissions'">
            {{ $t('addEditPlanMedia.tab.commissions') }}
          </li>
        </ul>
      </div>
    </div>
    <div class="corps">
      <component
        :is="activeTab"
        :detail="false"
        :plan-media="planMedia"
        />
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import { create, update, get } from '@/api/planMedia';
import dropdownButton from '@/components/general/button/dropdownButton';
import breadcrumb from '@/components/general/breadcrumb';
import errorMsgComponent from '@/components/general/errorMsgComponent';
import Information from '@/components/planMedia/edition/m_information';
import Evenement from '@/components/planMedia/evenement';
import inProgress from '@/services/general/inProgress';
import genererPlanMedia from '@/services/planMedia/genererPlanMedia';
import messageErreur from '@/services/general/messageErreur';
import messageSucces from '@/services/general/messageSucces';
import validationGeneral from '@/services/validation/validation';
import translate from '@/services/planMedia/translator';
import postGetPlanMedia from '@/services/planMedia/postGetPlanMedia';
import formatDate from '@/services/general/formatIsoDate';
import * as typesParametres from '@/store/modules/parametres-mutation-type';
import * as typesPlanMedia from '@/store/modules/planMedia-mutation-type';
import commissions from '@/components/planMedia/edition/commissions';
import validationPlanMedia from '@/services/planMedia/validationPlanMedia';
import infoVentes from '@/components/planMedia/infoVentes';
import addIntervenant from '@/services/devisCommandes/addIntervenant';
import creerIntervenant from '@/services/devisCommandes/creerIntervenant';
import { ADD_CLIENT_ROUTE_NAME, ECRAN_PLAN_MEDIA_EDITION } from '@/configuration/bodac/planMedia/planMediaConfig';
import { COMMANDES, DEVIS } from '@/configuration/permissionsBodacConf';
import { TYPE_DEVIS, TYPE_COMMANDE } from '@/configuration/devisConf';

const Societe = createNamespacedHelpers('societe');
const Attachments = createNamespacedHelpers('attachments');
const Parametres = createNamespacedHelpers('parametres');
const Permissions = createNamespacedHelpers('permissions');
const Clients = createNamespacedHelpers('clients');
const PlanMedia = createNamespacedHelpers('planMedia');

export default {
  name: 'm_planMedia',
  data() {
    return {
      error: false,
      activeTab: 'information',
      messages: [],
      messagesChampsVide: [],
      champsVide: [],
      libelle: null,
      permCommandes: COMMANDES,
      constantes: {
        TYPE_COMMANDE,
        TYPE_DEVIS,
      },
      planMedia: null,
      prevRoute: null,
    };
  },
  components: {
    breadcrumb,
    dropdownButton,
    errorMsgComponent,
    infoVentes,
    Information,
    Evenement,
    commissions,
  },
  computed: {
    ...Permissions.mapGetters({
      checkPermissions: 'checkPermissions',
    }),
    ...Societe.mapGetters({
      societeActiveId: 'societeActiveId',
      societeActive: 'societeActive',
    }),
    ...Parametres.mapGetters({
      parametre: 'parametre',
    }),
    ...Clients.mapGetters({
      clientCourant: 'clientCourant',
    }),
    ...PlanMedia.mapGetters({
      planMediaCourant: 'planMediaCourant',
    }),
    breadcrumb() {
      let routeName = 'm.presse.plan_media-add';
      if (this.$route.params.id) routeName = 'm.presse.plan_media-edit';
      return [
        { title: this.$i18n.t('addEditPlanMedia.breadcrumb.racine'), route: this.$PAGE_DEFAULT, isActive: false },
        { title: this.$i18n.t('addEditPlanMedia.breadcrumb.liste'), route: { name: 'l.presse.plan_media' }, isActive: false },
        { title: this.getTitle, route: { name: routeName }, isActive: true },
      ];
    },
    actionSecondary() {
      const actions = [
        {
          label: this.$t('addEditPlanMedia.commande.actions.addDevis'),
          action: () => { this.addDevisCommande(TYPE_DEVIS); },
          permissionAction: DEVIS.maj,
        },
      ];

      return actions;
    },
    getTitle() {
      return this.$route.params.id
        ? this.$i18n.t('addEditPlanMedia.breadcrumb.edit', { libelle: this.libelle })
        : this.$i18n.t('addEditPlanMedia.breadcrumb.add');
    },
  },
  methods: {
    ...Attachments.mapActions({
      setAttachmentContext: 'setContext',
      setAttachmentElementId: 'setElementId',
      insertAttachments: 'insert',
      updateAttachments: 'update',
    }),
    ...Permissions.mapActions({
      showPopUpPermission: 'showPopup',
    }),
    ...Parametres.mapActions({
      loadParametreUtilisateur: 'loadParametreUtilisateur',
    }),
    ...Clients.mapActions({
      setClientCourant: 'setClientCourant',
    }),
    ...PlanMedia.mapMutations({
      setPlanMediaCourant: typesPlanMedia.SET_PLAN_MEDIA_COURANT,
    }),
    inProgress() {
      inProgress(this.$i18n);
    },
    preSaveOrUpdatePlanMedia() {
      const planMedia = cloneDeep(this.planMedia);
      planMedia.societeId = this.societeActiveId;
      const index = planMedia.intervenants
        .findIndex(i => !i.nomCommercial);
      if (index !== -1) planMedia.intervenants.splice(index, 1);
      planMedia.dateDebut = formatDate(planMedia.dateDebut);
      planMedia.dateFin = formatDate(planMedia.dateFin);
      planMedia.datePM = formatDate(planMedia.datePM);
      planMedia.dateSignature = formatDate(planMedia.dateSignature);
      planMedia.evenements.forEach(evenement => {
        if (evenement.dateEvenement) evenement.dateEvenement = formatDate(evenement.dateEvenement);
        if (evenement.isCreate) delete evenement.id;
      });
      return planMedia;
    },
    async save(returnResult = false) {
      this.champsVide = [];
      this.messagesChampsVide = [];
      this.messages = [];
      const loadingComponent = this.$buefy.loading.open();
      try {
        let result = null;
        const planMedia = this.preSaveOrUpdatePlanMedia();
        if (this.$route.params.id) {
          result = (await update(planMedia)).data.result;
          await this.insertAttachments(result.id);
          messageSucces(this.$t('addEditPlanMedia.notifications.successChange'));
        } else {
          result = (await create(planMedia)).data.result;
          await this.updateAttachments(result.id);
          messageSucces(this.$t('addEditPlanMedia.notifications.successCreation'));
        }
        await this.setAttachmentElementId({ id: result.id });
        loadingComponent.close();
        if (returnResult) return result;
        await this.$router.push({
          name: 'c.presse.plan_media',
          params: { id: result.id },
        });
        return result;
      } catch (e) {
        if (e.response.data.error) {
          this.error = true;
          validationPlanMedia(e.response, this.messages);
          validationGeneral(this.$i18n, e.response, this.champsVide,
            this.messages, this.messagesChampsVide, translate);
          loadingComponent.close();
          return null;
        }
        messageErreur(this.$i18n);
        loadingComponent.close();
        throw e;
      }
    },
    async addDevisCommande(type) {
      let nameRoute = '';
      if (type === TYPE_COMMANDE) nameRoute = 'm.bodac.commandes-planMedia';
      if (type === TYPE_DEVIS) nameRoute = 'm.bodac.devis-planMedia';
      const loadingComponent = this.$buefy.loading.open();
      try {
        const planMediaId = (await this.save(true)).id;
        loadingComponent.close();
        this.$router.push({
          name: nameRoute,
          params: {
            planMediaId,
            typeEcran: ECRAN_PLAN_MEDIA_EDITION,
          },
        });
      } catch (e) {
        loadingComponent.close();
        throw e;
      }
    },
    cancel() {
      if (this.prevRoute?.name === ADD_CLIENT_ROUTE_NAME) {
        this.$router.push({ name: 'l.presse.plan_media' });
        return;
      }
      if (this.$route.params.id) {
        this.$router.push({
          name: 'c.presse.plan_media',
          params: { id: this.$route.params.id },
        });
      } else {
        this.$router.push({ name: 'l.presse.plan_media' });
      }
    },
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open();
    try {
      await this.setAttachmentContext({ module: 'PlanMedia' });
      await this.loadParametreUtilisateur();
      const users = this.parametre(typesParametres.PARAMETRES_KEYS.utilisateur);
      let planMedia = {};
      if (this.planMediaCourant) {
        planMedia = this.planMediaCourant;
        this.setPlanMediaCourant(null);
      } else if (this.$route.params.id) {
        planMedia = (await get(this.$route.params.id)).data.result;
        this.setInformationPage(this.$t('pageGeneral.modification', { item: planMedia.code }));
        await this.setAttachmentElementId({ id: planMedia.id });
      } else {
        this.setInformationPage(this.$t('pagePresse.planMedia.creation'));
        planMedia = genererPlanMedia();
        planMedia.societeId = this.societeActiveId;
      }
      this.planMedia = postGetPlanMedia(planMedia, users);
      this.planMedia.commandes = [...planMedia.commandes, ...planMedia.devis];
      if (this.clientCourant) {
        addIntervenant(this.planMedia, creerIntervenant(this.clientCourant));
        this.setClientCourant(null);
      }
      this.libelle = this.planMedia.code;
      loadingComponent.close();
    } catch (e) {
      loadingComponent.close();
      messageErreur(this.$i18n);
      throw e;
    }
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.prevRoute = from;
    });
  },
  beforeRouteUpdate() {
    this.setPlanMediaCourant(null);
  },
  beforeRouteLeave(to, from, next) {
    if (to.name === ADD_CLIENT_ROUTE_NAME) {
      this.setPlanMediaCourant(this.preSaveOrUpdatePlanMedia(this.planMedia));
    } else {
      this.setPlanMediaCourant(null);
    }
    next();
  },
};
</script>

<style scoped>

</style>
